// The moments (issue #48): what deserves two or three seconds of the player's attention — a relay that lights up, a
// first contact, the welcome Draw (#47) — and how long to give them. The simulation says what happened (`relay.ready`, `contact.first`);
// this decides what to play, full the first time, short afterwards, a fade only for `prefers-reduced-motion`.
// Game.tsx plays; nothing here needs a browser, so it is tested as is.
import type { PlayerView } from '@aurane/sim';

export type MomentKind = 'relay' | 'contact' | 'welcome' | 'wormhole' | 'draw';
/** `draw` (issue #48, idea 5): the hourly reveal — `bands` drawn, `index` of the Draw, `lit` = my connected systems in
 *  those bands, band by band (they light up one band at a time). */
export interface Moment { key: string; kind: MomentKind; system: string; at: number; path: string[]; bands?: number[]; index?: number; lit?: string[][] }

/** How a moment plays. `camera`: the map glides to the star; `run`: a pulse of Signal runs to the capital. */
export interface MomentPlan { camera: boolean; run: boolean; cameraMs: number; runMs: number; titleMs: number }

type V = Pick<PlayerView, 'me' | 'events' | 'relays'> & Partial<Pick<PlayerView, 'systems'>>;

const KIND: Record<string, MomentKind> = { 'relay.ready': 'relay', 'contact.first': 'contact', 'wormhole.found': 'wormhole' };

/** The moments among these events, oldest first, that were not seen yet (`seen` holds their keys). */
export function newMoments(v: V, seen: ReadonlySet<string>): Moment[] {
  const out: Moment[] = [];
  for (const e of v.events) {
    // The hourly Draw is a rendezvous (issue #48, idea 5): the bands come out one by one, my systems in them light up.
    if (e.kind === 'draw') {
      const d = (e.data ?? {}) as { bands?: unknown; index?: unknown };
      const bands = Array.isArray(d.bands) ? (d.bands as number[]) : [];
      const key = `${e.at}|draw`;
      if (!bands.length || seen.has(key)) continue;
      const mine = (v.systems ?? []).filter((s) => s.owner === v.me.id && s.connected);
      out.push({ key, kind: 'draw', system: v.me.capital, at: e.at, path: [], bands, index: Number(d.index ?? 0), lit: bands.map((b) => mine.filter((s) => s.band === b).map((s) => s.id)) });
      continue;
    }
    // The welcome Draw (issue #47) plays at the capital; other recaps are no moment.
    const welcome = e.kind === 'draw.recap' && (e.data as { welcome?: boolean } | undefined)?.welcome === true;
    const kind = welcome ? 'welcome' : KIND[e.kind];
    const system = welcome ? v.me.capital : (e.data as { system?: unknown } | undefined)?.system;
    if (!kind || e.actors[0] !== v.me.id || typeof system !== 'string') continue;
    const key = `${e.at}|${e.kind}|${system}`;
    if (seen.has(key)) continue;
    out.push({ key, kind, system, at: e.at, path: kind === 'relay' ? pathToCapital(v, system) : [] });
  }
  return out;
}

/** The stars from `from` to the capital along my live relays (shortest hops), [] when not connected. */
export function pathToCapital(v: Pick<PlayerView, 'me' | 'relays'>, from: string): string[] {
  const adj = new Map<string, string[]>();
  for (const r of v.relays) {
    if (r.owner !== v.me.id || !r.ready || r.cut) continue;
    (adj.get(r.a) ?? adj.set(r.a, []).get(r.a)!).push(r.b);
    (adj.get(r.b) ?? adj.set(r.b, []).get(r.b)!).push(r.a);
  }
  const prev = new Map<string, string | null>([[from, null]]);
  const queue = [from];
  for (let i = 0; i < queue.length; i++) {
    const at = queue[i]!;
    if (at === v.me.capital) break;
    for (const n of adj.get(at) ?? []) if (!prev.has(n)) { prev.set(n, at); queue.push(n); }
  }
  if (!prev.has(v.me.capital)) return [];
  const back: string[] = [];
  for (let at: string | null = v.me.capital; at !== null; at = prev.get(at) ?? null) back.push(at);
  return back.reverse();
}

/** The Draw's reveal: the map does not move (the player may be anywhere), the bands take three seconds. */
export function drawPlan(reduced: boolean): MomentPlan {
  return reduced ? { camera: false, run: false, cameraMs: 0, runMs: 0, titleMs: 1600 } : { camera: false, run: false, cameraMs: 0, runMs: 0, titleMs: 3200 };
}

/** Full the first time a kind plays, short afterwards; a fade only (no camera, no run) when motion is reduced. */
export function momentPlan(timesPlayed: number, reduced: boolean): MomentPlan {
  if (reduced) return { camera: false, run: false, cameraMs: 0, runMs: 0, titleMs: 1600 };
  if (timesPlayed === 0) return { camera: true, run: true, cameraMs: 900, runMs: 1600, titleMs: 2800 };
  return { camera: true, run: true, cameraMs: 450, runMs: 800, titleMs: 1400 };
}

const COUNT_KEY = 'aurane.moments';

/** How many times each kind has played on this device (full the first time only). */
export function playedCounts(): Record<MomentKind, number> {
  try { const raw = JSON.parse(localStorage.getItem(COUNT_KEY) ?? '{}') as Partial<Record<MomentKind, number>>; return { relay: raw.relay ?? 0, contact: raw.contact ?? 0, welcome: raw.welcome ?? 0, wormhole: raw.wormhole ?? 0, draw: raw.draw ?? 0 }; } catch { return { relay: 0, contact: 0, welcome: 0, wormhole: 0, draw: 0 }; }
}

export function countPlayed(kind: MomentKind): void {
  const c = playedCounts();
  c[kind]++;
  try { localStorage.setItem(COUNT_KEY, JSON.stringify(c)); } catch { /* private mode: every moment stays full */ }
}

export const prefersReducedMotion = (): boolean => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};
